import type {
  DActionRow,
  DAttachment,
  DButton,
  DContainer,
  DEmbed,
  DField,
  DFile,
  DGallery,
  DMediaItem,
  DPoll,
  DSection,
  DSelect,
  DSelectOption,
  DSeparator,
  DText,
  Draft,
} from './types.js';

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

export function uid(len = 8): string {
  const bytes = new Uint8Array(len);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
  return out;
}

export function newDraft(mode: Draft['mode'] = 'classic'): Draft {
  return {
    version: 1,
    mode,
    content: '',
    embeds: [],
    rows: [],
    v2: [],
    poll: null,
    attachments: [],
    profile: { username: '', avatarUrl: '' },
    options: { silent: false, suppressEmbeds: false, mentions: { everyone: false, roles: true, users: true } },
  };
}

export function newEmbed(): DEmbed {
  return {
    id: uid(),
    color: 0x7c5cff,
    title: '',
    url: '',
    description: '',
    author: { name: '', url: '', iconUrl: '' },
    footer: { text: '', iconUrl: '' },
    timestamp: null,
    image: '',
    thumbnail: '',
    fields: [],
  };
}

export function newField(): DField {
  return { id: uid(), name: '', value: '', inline: true };
}

export function newButton(style: DButton['style'] = 'primary'): DButton {
  return {
    id: uid(),
    type: 'button',
    style,
    label: 'Кнопка',
    emoji: '',
    url: style === 'link' ? 'https://discord.com' : '',
    disabled: false,
    action: { type: 'none' },
  };
}

export function newSelectOption(n = 1): DSelectOption {
  return { id: uid(), label: `Вариант ${n}`, description: '', emoji: '', isDefault: false, action: { type: 'none' } };
}

export function newSelect(): DSelect {
  return {
    id: uid(),
    type: 'select',
    placeholder: 'Выберите вариант',
    minValues: 1,
    maxValues: 1,
    disabled: false,
    options: [newSelectOption(1)],
  };
}

export function newRow(withButton = true): DActionRow {
  return { id: uid(), type: 'row', items: withButton ? [newButton()] : [] };
}

export function newText(content = ''): DText {
  return { id: uid(), type: 'text', content };
}

export function newSection(): DSection {
  return {
    id: uid(),
    type: 'section',
    texts: [newText('Текст секции')],
    accessory: { type: 'thumbnail', url: '', description: '', spoiler: false },
  };
}

export function newMediaItem(url = ''): DMediaItem {
  return { id: uid(), url, description: '', spoiler: false };
}

export function newGallery(): DGallery {
  return { id: uid(), type: 'gallery', items: [newMediaItem()] };
}

export function newFile(attachment = ''): DFile {
  return { id: uid(), type: 'file', attachment, spoiler: false };
}

export function newSeparator(): DSeparator {
  return { id: uid(), type: 'separator', divider: true, spacing: 1 };
}

export function newContainer(): DContainer {
  return { id: uid(), type: 'container', accent: 0x7c5cff, spoiler: false, children: [newText('## Заголовок\nТекст внутри контейнера')] };
}

export function newPoll(): DPoll {
  return {
    question: 'Вопрос опроса',
    answers: [
      { id: uid(), text: 'Да', emoji: '' },
      { id: uid(), text: 'Нет', emoji: '' },
    ],
    durationHours: 24,
    multiselect: false,
  };
}

export function newAttachment(name: string, source: string): DAttachment {
  return { id: uid(), name, source, spoiler: false, description: '' };
}

/** Глубокая копия с новыми id у всех элементов (для «Дублировать»). */
export function cloneWithNewIds<T>(value: T): T {
  const copy = JSON.parse(JSON.stringify(value));
  const walk = (v: any) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') {
      if (typeof v.id === 'string' && v.id.length === 8) v.id = uid();
      Object.values(v).forEach(walk);
    }
  };
  walk(copy);
  return copy;
}

/** Приводит произвольный JSON к валидной структуре Draft (на случай старых шаблонов). */
export function normalizeDraft(input: any): Draft {
  const base = newDraft();
  if (!input || typeof input !== 'object') return base;
  const d: Draft = {
    ...base,
    ...input,
    version: 1,
    mode: input.mode === 'v2' ? 'v2' : 'classic',
    content: typeof input.content === 'string' ? input.content : '',
    embeds: Array.isArray(input.embeds) ? input.embeds : [],
    rows: Array.isArray(input.rows) ? input.rows : [],
    v2: Array.isArray(input.v2) ? input.v2 : [],
    poll: input.poll && typeof input.poll === 'object' ? input.poll : null,
    attachments: Array.isArray(input.attachments) ? input.attachments : [],
    profile: { ...base.profile, ...(input.profile || {}) },
    options: {
      ...base.options,
      ...(input.options || {}),
      mentions: { ...base.options.mentions, ...(input.options?.mentions || {}) },
    },
  };
  return d;
}
